import { AgTechButtonProps, AgTechButton } from "@agtech/web/components/Buttons/AgTechButtons"
import AgTechFadeInView from "@agtech/web/components/Pages/Loading/AgTechFadeInView"
import AgTechMultiStepFormDialog from "@agtech/web/components/Portals/Dialogs/AgTechMultiStepFormDialog"
import { useInvoiceDataCreateInvoiceAction, CreateInvoiceActionData } from "@sales/data/invoicing/InvoicingDataActions"
import { SaleLot } from "@sales/data/lots/LotData"
import { ContactCredit } from "@sales/data/operation/contacts/credits/ContactCreditData"
import { SaleInvoiceModifier } from "@sales/data/sales/InvoiceModifiers/SaleInvoiceModifierData"
import AgTechTourHelpButton from "@agtech/web/components/App/Tour/AgTechTourHelpButton"
import { InvoiceCreationDialogTourConfig } from "./InvoiceCreationDialogTourConfig"
import _ from "lodash"
import InvoiceInfoDialog from "../InvoiceInfoDialog"
import { useInvoiceDataGetBidderLotsToInvoiceQuery } from "@sales/data/invoicing/InvoicingDataQueries"
import { AgTechIndex } from "@agtech/core/utilities/AgTechUtilities"
import InvoiceInvoiceModifiersAndCreditsPage from "./pages/InvoiceCreationInvoiceModifiersAndCreditsPage"
import InvoiceCreationGeneralInfoPage from "./pages/InvoiceCreationGeneralInfoPage"
import InvoiceReviewAndCompletePage from "./pages/InvoiceCreationReviewAndCompletePage"
import BidderRegistrationDialog from "../../../bidders/dialogs/BidderRegistrationDialog"
import { useAgTechQuery, useAgTechQueryResultCombination } from "@agtech/core/data/queries/AgTechDataQueries"
import { useSaleContactDataGetContactDetailsQuery } from "@sales/data/operation/contacts/SaleContactDataQueries"
import { InvoiceModifierDataGetAllInvoiceModifiersQuery } from "@sales/data/sales/InvoiceModifiers/SaleInvoiceModifierDataQueries"
import { useCurrentSale } from "@sales/data/sales/SaleDataStore"
import { SaleContactDetails } from "@sales/data/operation/contacts/SaleContactData"
import { useAgTechWebAppContext } from "@agtech/web/components/App/Contexts/AgTechWebAppContext"

export type PotentialInvoicee = {
    bidderId: number,
    bidderNumber?: string,
    contactId?: number
}

export type Invoicee = {
    bidderId: number,
    contactId: number,
    bidderNumber?: string,
}

export type InvoiceCreationDialogProps = {
    invoicee: Invoicee
}

export type SaleInvoiceDialogFormData = {
    checkNumber: string,
    bidderId: number,
    contactId: number,
    bidderNumber?: string,
    selectedLots: SaleLot[],
    appliedInvoiceModifiers: AgTechIndex<SaleInvoiceModifier>,
    appliedCredits: AgTechIndex<ContactCredit>,
    date: Date
}

export type SaleInvoiceDialogFormContext = {
    contactDetails: SaleContactDetails,
    allLotsPurchasedByContact: SaleLot[],
    saleInvoiceModifiers: AgTechIndex<SaleInvoiceModifier>
}

const InvoiceCreationDialog = (props: InvoiceCreationDialogProps) => {
    const appContext = useAgTechWebAppContext();
    const dialogQuery = useInvoiceCreationDialogQuery(props);
    const dialogAction = useInvoiceDataCreateInvoiceAction();

    return (
        <AgTechMultiStepFormDialog
            width={900}
            dialogData={{
                query: dialogQuery,
                getDialogData: d => {
                    const invoiceCreationFormData: SaleInvoiceDialogFormData = {
                        checkNumber: '',
                        bidderId: props.invoicee.bidderId,
                        bidderNumber: props.invoicee.bidderNumber,
                        contactId: props.invoicee.contactId,
                        selectedLots: d.lotsToInvoice,
                        appliedCredits: {},
                        appliedInvoiceModifiers: {},
                        date: new Date(),
                    };

                    return invoiceCreationFormData;
                },
                getDialogContext: d => ({
                    contactDetails: d.contactDetails,
                    allLotsPurchasedByContact: d.lotsToInvoice,
                    saleInvoiceModifiers: d.saleInvoiceModifiers
                })
            }}
            dialogAction={{
                action: dialogAction,
                getActionData: (d, context) => {
                    return {
                        checkNumber: d.checkNumber,
                        bidderId: d.bidderId,
                        contact: context.contactDetails.contact,
                        contactId: d.contactId,
                        lots: d.selectedLots.map(l => l.id),
                        credits: Object.values(d.appliedCredits).map(c => c.id),
                        InvoiceModifiers: Object.values(d.appliedInvoiceModifiers).map(d => d.id),
                        date: new Date()
                    };
                }
            }}
            structure={d => ({
                title: 'Create Invoice - ' + (props.invoicee.bidderNumber ?? 'N/A'),
                details: `To create a new invoice for the selected bidder, please follow the steps below`,
                submitButtonText: 'Create Invoice',
                headerContent: () => <AgTechTourHelpButton tourConfig={InvoiceCreationDialogTourConfig} />,
                steps: [
                    {
                        name: 'Info & Lots',
                        body: formContext => <InvoiceCreationGeneralInfoPage formContext={formContext} />
                    },
                    {
                        name: 'Charges, Discounts & Credits',
                        body: formContext => <InvoiceInvoiceModifiersAndCreditsPage formContext={formContext} />
                    },
                    {
                        name: 'Review & Create',
                        body: formContext => <InvoiceReviewAndCompletePage formContext={formContext} />
                    }
                ]
            })}
            behavior={{
                confirmation: {
                    renderFormDialogConfirmationView: async (data, actions) => {
                        await actions.grow(1000);
                        
                        return (
                            <AgTechFadeInView classes="stretched bg-white absolute z-10 flex">
                                <InvoiceInfoDialog
                                    invoiceId={data.responseData.invoiceId}
                                    invoiceDocument={data.responseData.invoiceDocument}
                                    actions={actions}
                                    showNextSteps={true}
                                    growAfterLoading={false}
                                />
                            </AgTechFadeInView>
                        )
                    }
                }
            }}
        />
    );
}

const useInvoiceCreationDialogQuery = (props: InvoiceCreationDialogProps) => {
    const currentSale = useCurrentSale();

    const invoiceContactDetailsQuery =
        useSaleContactDataGetContactDetailsQuery(props.invoicee.contactId);

    const lotsToInvoiceQuery = useInvoiceDataGetBidderLotsToInvoiceQuery({
        bidderId: props.invoicee.bidderId
    });

    const InvoiceModifiersQuery = useAgTechQuery({
        query: InvoiceModifierDataGetAllInvoiceModifiersQuery
    });

    /*const invoiceNumberQuery = useAgTechQuery({
        query: InvoiceDataGetNextInvoiceNumberQuery,
        queryContext: { saleId: currentSale.id }
    });*/

    const invoiceCombination = useAgTechQueryResultCombination({
        firstQuery: invoiceContactDetailsQuery,
        secondQuery: lotsToInvoiceQuery,
        projection: (contactDetails, lotsToInvoice) => {
            return {
                contactDetails,
                lotsToInvoice
            }
        }
    });

    return useAgTechQueryResultCombination({
        firstQuery: invoiceCombination,
        secondQuery: InvoiceModifiersQuery,
        projection: (invoiceData, saleData) => {
            return {
                contactDetails: invoiceData.contactDetails,
                lotsToInvoice: invoiceData.lotsToInvoice,
                saleInvoiceModifiers: saleData
            }
        }
    });
}

InvoiceCreationDialog.Button = (props: { potentialInvoicee: PotentialInvoicee } & AgTechButtonProps) => {
    const appContext = useAgTechWebAppContext();

    return (
        <AgTechButton
            text='Invoice'
            icon='fas fa-plus'
            {...props}
            action={async e => {
                if (props.potentialInvoicee.contactId) {
                    appContext.dialogs.openDialog({
                        dialog: () => (
                            <InvoiceCreationDialog
                                invoicee={{ 
                                    ...props.potentialInvoicee,
                                    contactId: props.potentialInvoicee.contactId!
                                }}
                            />
                        )
                    });
                }
                else {
                    const shouldRegisterBidder = await e.confirmations.askForConfirmation({
                        content: {
                            header: `Bidder ${props.potentialInvoicee.bidderNumber} is not registered in this sale.`,
                            details: 'Please register this bidder before invoicing',
                            confirmationButtonText: 'Register Bidder',
                        },
                        actions: {
                            onConfirm: async () => {
                                return true;
                            }
                        }
                    });

                    if (shouldRegisterBidder) {
                        const reconciledContactId = await new Promise<number>((res) => {
                            BidderRegistrationDialog.Open(e, {
                                bidder: {
                                    id: props.potentialInvoicee.bidderId,
                                    ...props.potentialInvoicee
                                },
                                onBidderRegistered: async reconciledBidderContact => {
                                    res(reconciledBidderContact.contact!.id);
                                }
                            });
                        });

                        if (reconciledContactId) {
                            appContext.dialogs.openDialog({
                                dialog: () => (
                                    <InvoiceCreationDialog
                                        invoicee={{
                                            ...props.potentialInvoicee,
                                            bidderId: props.potentialInvoicee.bidderId,
                                            contactId: reconciledContactId
                                        }}
                                    />
                                )
                            });
                        }
                    }
                }
            }}
        />
    )
}

export default InvoiceCreationDialog